/**
 * Onglet Checklist TI
 * Gestion des items de checklist de préparation des réunions
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, Plus, Edit2, Trash2, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  getChecklistItems,
  createChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  toggleChecklistItemStatus,
  createDefaultChecklistItems,
  type ChecklistItem,
} from '../services/checklistService';
import { getPartiesPrenantes, type PartiePrenante } from '../services/partiesPrenantesService';
import { DEFAULT_CLIENT_ID } from '../config/client';

interface ChecklistTabProps {
  reunionId: number;
  onUpdate: () => void;
  showToast: (message: string, type: 'success' | 'error') => void;
}

export function ChecklistTab({ reunionId, onUpdate, showToast }: ChecklistTabProps) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<ChecklistItem>>({});
  const [partiesPrenantes, setPartiesPrenantes] = useState<PartiePrenante[]>([]);
  const [newItem, setNewItem] = useState<Partial<ChecklistItem>>({
    titre: '',
    description: '',
    ordre: 0,
    statut: 'Non Validé',
    responsable: '',
    est_obligatoire: true,
  });

  useEffect(() => {
    loadItems();
    loadPartiesPrenantes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reunionId]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getChecklistItems(reunionId);
      setItems(data);
    } catch (error) {
      console.error('Erreur chargement checklist:', error);
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    try {
      setLoading(true);
      const defaultItems = await createDefaultChecklistItems(reunionId, 'Créateur de la réunion');
      setItems(defaultItems);
      showToast('Items par défaut créés avec succès', 'success');
      onUpdate();
    } catch (error) {
      console.error('Erreur création items par défaut:', error);
      showToast('Erreur lors de la création des items par défaut', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPartiesPrenantes = async () => {
    try {
      const pp = await getPartiesPrenantes(DEFAULT_CLIENT_ID);
      setPartiesPrenantes(pp);
    } catch (error) {
      console.error('Erreur chargement parties prenantes:', error);
    }
  };

  const handleAdd = async () => {
    try {
      if (!newItem.titre) {
        showToast('Le titre est requis', 'error');
        return;
      }
      await createChecklistItem({ ...newItem, reunion_id: reunionId });
      showToast('Item ajouté!', 'success');
      setShowAddForm(false);
      setNewItem({
        titre: '',
        description: '',
        ordre: items.length + 1,
        statut: 'Non Validé',
        responsable: '',
        est_obligatoire: true,
      });
      await loadItems();
      onUpdate();
    } catch (error) {
      console.error('Erreur ajout item checklist:', error);
      showToast("Erreur lors de l'ajout", 'error');
    }
  };

  const handleEdit = (item: ChecklistItem) => {
    setEditingId(item.id || null);
    setEditData({
      titre: item.titre,
      description: item.description,
      ordre: item.ordre,
      responsable: item.responsable,
      est_obligatoire: item.est_obligatoire,
    });
  };

  const handleSaveEdit = async () => {
    try {
      if (!editData.titre) {
        showToast('Le titre est requis', 'error');
        return;
      }
      if (editingId) {
        await updateChecklistItem(editingId, editData);
        showToast('Item mis à jour!', 'success');
        setEditingId(null);
        setEditData({});
        await loadItems();
        onUpdate();
      }
    } catch (error) {
      console.error('Erreur update item checklist:', error);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleDelete = async (itemId: number, titre: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${titre}"?`)) return;
    try {
      await deleteChecklistItem(itemId);
      showToast('Item supprimé!', 'success');
      await loadItems();
      onUpdate();
    } catch (error) {
      console.error('Erreur delete item checklist:', error);
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleToggleStatus = async (item: ChecklistItem) => {
    try {
      const newStatus = item.statut === 'À Valider' ? 'Non Validé' : 'À Valider';
      await toggleChecklistItemStatus(item.id!, newStatus);
      showToast(`Statut changé: ${newStatus}`, 'success');
      await loadItems();
      onUpdate();
    } catch (error) {
      console.error('Erreur toggle checklist:', error);
      showToast('Erreur lors du changement de statut', 'error');
    }
  };

  const validatedCount = items.filter((i) => i.statut === 'À Valider').length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? Math.round((validatedCount / totalCount) * 100) : 0;
  const allValidated = totalCount > 0 && validatedCount === totalCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <CheckSquare className="w-7 h-7 text-yellow-600" />
            Checklist de Préparation
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {validatedCount}/{totalCount} validés • {progressPercent}% complété
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un item
          </button>
        </div>
      </div>

      {totalCount > 0 && (
        <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-yellow-500 to-green-600 h-full flex items-center justify-center text-xs text-white font-bold"
          >
            {progressPercent}%
          </motion.div>
        </div>
      )}

      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6"
        >
          <h4 className="font-bold text-gray-800 mb-4">Nouvel Item de Checklist</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
              <input
                type="text"
                value={newItem.titre}
                onChange={(e) => setNewItem({ ...newItem, titre: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                placeholder="Ex: Ordre du jour préparé"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                rows={2}
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                placeholder="Détails de l'item..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ordre</label>
              <input
                type="number"
                min="0"
                value={newItem.ordre || items.length + 1}
                onChange={(e) => setNewItem({ ...newItem, ordre: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Responsable</label>
              <select
                value={newItem.responsable}
                onChange={(e) => setNewItem({ ...newItem, responsable: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">Sélectionner...</option>
                {partiesPrenantes.map((pp) => (
                  <option key={pp.id} value={`${pp.prenom} ${pp.nom}`}>
                    {pp.prenom} {pp.nom} - {pp.titre_poste}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 pt-7">
              <input
                type="checkbox"
                checked={newItem.est_obligatoire !== false}
                onChange={(e) => setNewItem({ ...newItem, est_obligatoire: e.target.checked })}
                className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
              />
              <label className="text-sm font-medium text-gray-700">Obligatoire</label>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleAdd}
              className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
            >
              Ajouter
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
          </div>
        </motion.div>
      )}

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => {
            const isEditing = editingId === item.id;
            const isValidated = item.statut === 'À Valider';

            return (
              <div
                key={item.id}
                className={`bg-white border-2 rounded-lg p-4 transition-colors group ${
                  isEditing
                    ? 'border-yellow-500'
                    : isValidated
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 hover:border-yellow-300'
                }`}
              >
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                        <input
                          type="text"
                          value={editData.titre}
                          onChange={(e) => setEditData({ ...editData, titre: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          rows={2}
                          value={editData.description || ''}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                        <input
                          type="number"
                          min="0"
                          value={editData.ordre || 0}
                          onChange={(e) => setEditData({ ...editData, ordre: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Responsable</label>
                        <select
                          value={editData.responsable || ''}
                          onChange={(e) => setEditData({ ...editData, responsable: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="">Sélectionner...</option>
                          {partiesPrenantes.map((pp) => (
                            <option key={pp.id} value={`${pp.prenom} ${pp.nom}`}>
                              {pp.prenom} {pp.nom}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2 pt-7">
                        <input
                          type="checkbox"
                          checked={editData.est_obligatoire !== false}
                          onChange={(e) =>
                            setEditData({ ...editData, est_obligatoire: e.target.checked })
                          }
                          className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                        />
                        <label className="text-sm font-medium text-gray-700">Obligatoire</label>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveEdit}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Sauvegarder
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(null);
                          setEditData({});
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3 flex-1">
                        <button
                          onClick={() => handleToggleStatus(item)}
                          className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                            isValidated
                              ? 'bg-green-500 border-green-600 text-white'
                              : 'bg-white border-gray-300 hover:border-yellow-500'
                          }`}
                          title={isValidated ? 'Marquer comme non validé' : 'Marquer comme validé'}
                        >
                          {isValidated && <CheckCircle2 className="w-4 h-4" />}
                        </button>
                        <div className="flex-1">
                          <h4
                            className={`font-bold ${
                              isValidated ? 'text-green-800 line-through' : 'text-gray-800'
                            }`}
                          >
                            {item.titre}
                            {item.est_obligatoire && (
                              <span className="ml-2 text-red-500 text-xs" title="Obligatoire">
                                <AlertCircle className="w-4 h-4 inline" />
                              </span>
                            )}
                          </h4>
                          {item.description && (
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold ${
                            isValidated ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {item.statut}
                        </span>
                        <button
                          onClick={() => handleEdit(item)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-yellow-100 text-yellow-600"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => item.id && handleDelete(item.id, item.titre)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-red-100 text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {item.responsable && (
                        <div className="flex items-center gap-1">
                          <span>👤</span>
                          <span>{item.responsable}</span>
                        </div>
                      )}
                      {item.ordre !== undefined && item.ordre > 0 && (
                        <div className="flex items-center gap-1">
                          <span>#</span>
                          <span>{item.ordre}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">Aucun item de checklist</p>
          <p className="text-sm text-gray-400 mb-4">
            Les items par défaut sont créés automatiquement lors de la création de la réunion.
          </p>
          <button
            onClick={handleCreateDefaults}
            className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
          >
            Créer les items par défaut
          </button>
        </div>
      )}

      {allValidated && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 shadow-lg border-2 border-green-400">
            <div className="flex items-center justify-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-white" />
              <div className="text-center">
                <h4 className="text-xl font-bold text-white mb-1">✅ Tout est validé !</h4>
                <p className="text-green-100 text-sm">
                  Tous les items de la checklist sont validés. La réunion est prête.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

